<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import { clinicalDrugsApi, type Substance } from '@/api/clinical-drugs';
import {
  medicationsApi, type AllergyForm, type AllergySeverity, type PatientAllergy,
} from '@/api/medications';
import { allergyStatus } from '@/utils/allergies';
import { apiErrorText } from '@/utils/api-errors';
import ResponsiveDialog from '@/components/responsive-dialog.vue';
import ClinicalAuthorLine from '@/components/clinical/clinical-author-line.vue';

// Records a patient's allergies: to a substance of the catalog (new orders
// are checked against it) or in words (a drug class, a food: never checked).
// With none kept, "Sin alergias conocidas" says somebody asked. Removing an
// allergy keeps it in the history.
const props = defineProps<{ patientId: number; allergies: PatientAllergy[] }>();
const emit = defineEmits<{ close: [] }>();

const SEARCH_DELAY_MS = 300;
const MIN_SEARCH_LENGTH = 3;
const SEVERITIES: { value: AllergySeverity; title: string }[] = [
  { value: 'severe', title: 'Grave' },
  { value: 'mild', title: 'Leve' },
  { value: 'unknown', title: 'No indicada' },
];

const queryClient = useQueryClient();
const error = ref('');
const showErrors = ref(false);
// The allergy whose removal waits for a second click.
const confirming = ref<number | null>(null);

const source = ref<'catalog' | 'words'>('catalog');
const substance = ref<Substance | null>(null);
const description = ref('');
const reaction = ref('');
const severity = ref<AllergySeverity>('unknown');

const kept = computed(() => props.allergies.filter((row) => row.kind === 'allergy'));
const noneKnown = computed(() => props.allergies.find((row) => row.kind === 'none_known') ?? null);
const status = computed(() => allergyStatus(props.allergies));

const search = ref('');
const term = ref('');
let searchTimer: ReturnType<typeof setTimeout> | undefined;
// Picking a substance writes its name in the field: that is not a new search.
watch(search, (value) => {
  clearTimeout(searchTimer);
  if (substance.value && value === substance.value.name) return;
  searchTimer = setTimeout(() => {
    term.value = (value ?? '').trim();
  }, SEARCH_DELAY_MS);
});
onBeforeUnmount(() => clearTimeout(searchTimer));

const searchable = computed(() => term.value.length >= MIN_SEARCH_LENGTH);
const { data: substances, isFetching } = useQuery({
  queryKey: ['substances', term],
  queryFn: () => clinicalDrugsApi.searchSubstances(term.value),
  enabled: searchable,
  initialData: [],
});

// While typing or loading, say so instead of flashing "no results".
const typed = computed(() => (search.value ?? '').trim());
const noDataText = computed(() => {
  if (typed.value === substance.value?.name) return '';
  if (typed.value.length < MIN_SEARCH_LENGTH) return 'Escribe al menos 3 letras';
  if (isFetching.value || typed.value !== term.value) return 'Buscando...';

  return `No encontramos «${typed.value}». Regístrala como «Otra».`;
});

const missing = computed(() => (
  source.value === 'catalog' ? !substance.value : !description.value.trim()
));

function refresh() {
  error.value = '';
  queryClient.invalidateQueries({ queryKey: ['patientAllergies', props.patientId] });
}

function reset() {
  substance.value = null;
  search.value = '';
  description.value = '';
  reaction.value = '';
  severity.value = 'unknown';
  showErrors.value = false;
}

function failed(failure: unknown) {
  error.value = apiErrorText(failure, 'No se pudo guardar la alergia.');
}

const record = useMutation({
  mutationFn: (form: AllergyForm) => medicationsApi.recordAllergy(props.patientId, form),
  onSuccess: () => {
    reset();
    refresh();
  },
  onError: failed,
});

const remove = useMutation({
  mutationFn: (allergy: PatientAllergy) => medicationsApi.removeAllergy(props.patientId, allergy.id),
  onSuccess: () => {
    confirming.value = null;
    refresh();
  },
  onError: failed,
});

function add() {
  if (missing.value) {
    showErrors.value = true;

    return;
  }
  record.mutate({
    kind: 'allergy',
    substanceId: source.value === 'catalog' ? substance.value?.id : null,
    description: source.value === 'words' ? description.value : '',
    reaction: reaction.value,
    severity: severity.value,
  });
}
</script>
<template>
  <responsive-dialog
    :model-value="true"
    title="Alergias"
    max-width="640"
    scrollable
    @close="emit('close')"
  >
    <div class="d-flex flex-column ga-4 pt-2">
      <v-alert
        v-if="error"
        type="error"
        variant="tonal"
        :text="error"
        closable
        @click:close="error = ''"
      />

      <v-list
        v-if="kept.length > 0"
        lines="two"
        density="compact"
        class="py-0"
      >
        <v-list-item
          v-for="allergy in kept"
          :key="allergy.id"
          class="px-0"
        >
          <v-list-item-title>
            {{ allergy.name }}
            <v-chip
              v-if="allergy.severity !== 'unknown'"
              :color="allergy.severity === 'severe' ? 'error' : 'warning'"
              size="x-small"
              variant="tonal"
              class="ml-1"
            >
              {{ allergy.humanSeverity }}
            </v-chip>
          </v-list-item-title>
          <v-list-item-subtitle>
            <span v-if="allergy.reaction">{{ allergy.reaction }}</span>
            <clinical-author-line
              :author="allergy.author"
              :at="allergy.humanCreatedAt"
            />
          </v-list-item-subtitle>
          <template #append>
            <div
              v-if="confirming === allergy.id"
              class="d-flex ga-1"
            >
              <v-btn
                variant="text"
                size="small"
                @click="confirming = null"
              >
                Cancelar
              </v-btn>
              <v-btn
                color="error"
                variant="tonal"
                size="small"
                :loading="remove.isPending.value"
                @click="remove.mutate(allergy)"
              >
                Sí, quitar
              </v-btn>
            </div>
            <v-btn
              v-else
              variant="text"
              size="small"
              :aria-label="`Quitar la alergia a ${allergy.name}`"
              @click="confirming = allergy.id"
            >
              Quitar
            </v-btn>
          </template>
        </v-list-item>
      </v-list>
      <div
        v-else-if="noneKnown"
        class="d-flex align-center ga-2"
      >
        <span class="text-body-2">Sin alergias conocidas</span>
        <clinical-author-line
          :author="noneKnown.author"
          :at="noneKnown.humanCreatedAt"
        />
      </div>
      <p
        v-else
        class="text-body-2 text-medium-emphasis"
      >
        No hay alergias registradas.
      </p>

      <v-divider />
      <div class="text-subtitle-2 font-weight-bold">
        Agregar alergia
      </div>
      <v-btn-toggle
        v-model="source"
        mandatory
        density="compact"
        variant="outlined"
        divided
        color="primary"
      >
        <v-btn value="catalog">
          Medicamento o sustancia
        </v-btn>
        <v-btn value="words">
          Otra
        </v-btn>
      </v-btn-toggle>
      <v-autocomplete
        v-if="source === 'catalog'"
        v-model:search="search"
        v-model="substance"
        :items="searchable ? substances : []"
        :loading="isFetching"
        :no-data-text="noDataText"
        item-title="name"
        return-object
        no-filter
        clearable
        menu-icon=""
        prepend-inner-icon="mdi-magnify"
        label="Sustancia"
        placeholder="Ej.: penicilina"
        variant="outlined"
        density="compact"
        hint="Las indicaciones nuevas avisan si el medicamento la contiene."
        persistent-hint
        :error-messages="showErrors && missing ? 'Elige una sustancia' : ''"
      />
      <v-text-field
        v-else
        v-model="description"
        label="Alergia"
        placeholder="Ej.: penicilinas, mariscos, látex"
        variant="outlined"
        density="compact"
        hint="No se compara con los medicamentos: revísala al indicar."
        persistent-hint
        :error-messages="showErrors && missing ? 'Describe la alergia' : ''"
      />
      <div class="d-flex flex-wrap ga-3">
        <v-select
          v-model="severity"
          :items="SEVERITIES"
          label="Gravedad"
          variant="outlined"
          density="compact"
          hide-details
          class="severity-field"
        />
        <v-text-field
          v-model="reaction"
          label="Reacción (opcional)"
          placeholder="Ej.: urticaria, edema"
          variant="outlined"
          density="compact"
          hide-details
          class="reaction-field"
        />
      </div>
    </div>

    <template #actions>
      <v-btn
        v-if="status === 'unknown'"
        variant="text"
        :loading="record.isPending.value && record.variables.value?.kind === 'none_known'"
        @click="record.mutate({ kind: 'none_known' })"
      >
        Sin alergias conocidas
      </v-btn>
      <v-spacer />
      <v-btn @click="emit('close')">
        Cerrar
      </v-btn>
      <v-btn
        color="primary"
        variant="flat"
        :loading="record.isPending.value && record.variables.value?.kind === 'allergy'"
        @click="add"
      >
        Agregar
      </v-btn>
    </template>
  </responsive-dialog>
</template>
<style scoped>
.severity-field {
  flex: 0 0 180px;
}

.reaction-field {
  flex: 1 1 220px;
}
</style>
